import * as React from 'react';

import {Button} from 'shells/button/button';
import {FeatureUsage} from 'shells/limits/feature-usage';
import {Link} from 'shells/link/link';
import {FooterBar} from 'shells/modal/footer-bar';
import {Body} from 'shells/typography/body';

import styles from './feature-limits-modal-content.module.css';

type Props = {
    currentPlanLimit: number | null | undefined;
    currentPlanName: string;
    currentUsage: number;
    humanReadableType: string;
    upgradePlanName: string;
    upgradePlanLimit: number | null | undefined;
    tierPlanLimit: number | null | undefined;
    displayString?: string;
    hasFurtherLimits?: boolean;
    onClose: () => void;
};

export function FeatureLimitsModalContent(props: Props) {
    const {hasFurtherLimits = true} = props;
    const verb = props.currentPlanLimit === 1 ? 'is' : 'are';
    const tierPlanLimitStr =
        typeof props.tierPlanLimit === 'number' ? props.tierPlanLimit.toString() : 'unlimited';
    const currentPlanLimitStr =
        typeof props.currentPlanLimit === 'number'
            ? props.currentPlanLimit.toString()
            : 'unlimited';

    return (
        <>
            <Body>
                {shouldShowCustomLimitMessage(props.tierPlanLimit, props.currentPlanLimit) ? (
                    <>
                        Your plan supports {tierPlanLimitStr} {props.humanReadableType}, but you
                        have been provided a custom limit of {currentPlanLimitStr}.{' '}
                    </>
                ) : (
                    <>
                        {' '}
                        You’ve created {props.currentUsage} out of the {tierPlanLimitStr}{' '}
                        {props.displayString} that {verb} included in your current plan,{' '}
                        {props.currentPlanName}.{' '}
                    </>
                )}
                {hasFurtherLimits ? (
                    <>
                        If you need to create more {props.humanReadableType} or access additional
                        features, you can upgrade to {props.upgradePlanName} or explore our other
                        plan options.
                    </>
                ) : (
                    <>
                        If you need to create more {props.humanReadableType} or access additional
                        features, reach out to support for further options.
                    </>
                )}
            </Body>
            <div className={styles['feature-usage-container']}>
                <FeatureUsage
                    currentUsage={props.currentUsage}
                    currentPlanMaxUsage={props.currentPlanLimit}
                    hasCustomLimit={shouldShowCustomLimitMessage(
                        props.tierPlanLimit,
                        props.currentPlanLimit
                    )}
                    nextPlanMaxUsage={props.upgradePlanLimit}
                    hasFurtherLimits={hasFurtherLimits}
                />
            </div>
            {hasFurtherLimits && (
                <FooterBar
                    left={
                        <Button size='big' onClick={props.onClose} variant='text-secondary'>
                            Not right now
                        </Button>
                    }
                    right={
                        <Link
                            size='big'
                            newTab={true}
                            href='/setup/billing/sales'
                            variant='button-primary'
                        >
                            See upgrade options
                        </Link>
                    }
                />
            )}
        </>
    );
}

// If there is a custom limit and it is higher than the current tier plan limit, show the custom limit message
// If tierPLanLimit is null, the user is on a plan that doesn't have a limit so return false
function shouldShowCustomLimitMessage(
    tierPlanLimit?: number | null,
    currentPlanLimit?: number | null
) {
    if (typeof tierPlanLimit === 'number' && typeof currentPlanLimit === 'number') {
        return tierPlanLimit < currentPlanLimit;
    }

    return false;
}
